import { theme as antdAlgorithms, type ThemeConfig } from 'antd';
import type { ThemeDesign } from '@/api/types';
import { familyStack } from './hubAssets';
import { palettes, radius, typography, type Palette, type ThemeMode } from './tokens';

/**
 * Maps the Data Room token palette onto an AntD v5 ThemeConfig. Unlike the analyst console we do NOT
 * use the compact algorithm — the applicant surface is deliberately roomy: 40px controls (large
 * touch targets for phones, WCAG 2.2 SC 2.5.8) and a 15px base.
 */
const RADIUS_SCALE = { SQUARE: 0.25, DEFAULT: 1, ROUND: 2 } as const;

export function buildAntdTheme(
  mode: ThemeMode,
  override?: Partial<Palette>,
  design?: ThemeDesign | null,
): ThemeConfig {
  const corner = RADIUS_SCALE[design?.radius ?? 'DEFAULT'];
  const p: Palette = { ...palettes[mode], ...override };
  const algorithm = mode === 'dark' ? antdAlgorithms.darkAlgorithm : antdAlgorithms.defaultAlgorithm;

  return {
    algorithm,
    token: {
      colorPrimary: p.primary,
      colorInfo: p.info,
      colorSuccess: p.positive,
      colorWarning: p.attention,
      colorError: p.negative,

      colorBgBase: p.bgBase,
      colorBgLayout: p.bgBase,
      colorBgContainer: p.bgContainer,
      colorBgElevated: p.bgElevated,
      colorBorder: p.border,
      colorBorderSecondary: p.borderSubtle,

      colorText: p.textPrimary,
      colorTextSecondary: p.textSecondary,
      colorTextTertiary: p.textTertiary,
      colorTextQuaternary: p.textTertiary,

      fontFamily: design?.bodyFamily ? familyStack(design.bodyFamily) : typography.fontFamily,
      fontFamilyCode: typography.fontFamilyMono,
      fontSize: typography.size,
      fontSizeSM: typography.sizeSm,
      fontSizeLG: typography.sizeLg,
      fontSizeHeading2: typography.sizeH2,
      fontSizeHeading3: typography.sizeH3,
      fontSizeHeading4: typography.sizeH4,
      fontSizeHeading5: typography.sizeH5,
      lineHeight: typography.lineHeight,

      borderRadius: radius.md * corner,
      borderRadiusLG: radius.lg * corner,
      borderRadiusSM: radius.sm * corner,

      // Large touch targets — this is a phone-first surface.
      controlHeight: 40,
      controlHeightLG: 48,
      wireframe: false,
      colorPrimaryBorder: p.focusRing,
    },
    components: {
      Button: { controlHeight: 44, paddingInline: 20, fontWeight: 600 },
      Input: { controlHeight: 44 },
      Select: { controlHeight: 44 },
      Steps: { titleLineHeight: 1.4 },
      Card: { colorBorderSecondary: p.border },
      Result: { titleFontSize: typography.sizeH3 },
      Alert: { withDescriptionPadding: '14px 16px' },
    },
  };
}
